import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import {
    OrderPaymentTransactionStatus,
    OrderPaymentTransactionStatusLabelMapping,
} from '../../../../../../core/enums/commerce/payment/order-payment-transaction-status';
import { PaymentMethod } from '../../../../../../core/enums/commerce/payment/payment-method';
import { parseDateToEasternGridFormat } from '../../../../../../core/functions/date-helpers';
import { PaymentHistoryGridRowData } from '../../models/payment-history-grid-row-data';
import { PaymentTransactionDetailsDialogComponent } from './components/payment-transaction-details-dialog/payment-transaction-details-dialog.component';
import { PaymentMessageCellRendererParams } from './constants/payment-message-cell-renderer-params';

@Component({
    selector: 'app-payment-message-cell-renderer',
    templateUrl: './payment-message-cell-renderer.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [],
})
export class PaymentMessageCellRendererComponent
    implements ICellRendererAngularComp
{
    constructor(private readonly dialog: MatDialog) {}

    params: PaymentMessageCellRendererParams =
        {} as PaymentMessageCellRendererParams;

    get orderId(): string | null {
        return this.params.orderId;
    }

    get paymentId(): string | null {
        return this.params.data?.id.toString() || null;
    }

    get paymentMessage(): string {
        const rowData = this.params.data as PaymentHistoryGridRowData;
        const message =
            OrderPaymentTransactionStatusLabelMapping[rowData.status];

        if (
            rowData.status === OrderPaymentTransactionStatus.Voided &&
            rowData.voidedAt
        ) {
            return `${message} at ${parseDateToEasternGridFormat(rowData.voidedAt)}`;
        }

        if (
            rowData.status === OrderPaymentTransactionStatus.Confirmed &&
            rowData.confirmedAt
        ) {
            return `${message} on ${parseDateToEasternGridFormat(rowData.confirmedAt)}`;
        }

        return message;
    }

    agInit(
        params: ICellRendererParams<PaymentHistoryGridRowData> &
            PaymentMessageCellRendererParams,
    ): void {
        this.params = params as PaymentMessageCellRendererParams;
    }

    refresh(): boolean {
        return false;
    }

    isAuthorizeNetPayment(): boolean {
        return this.params.data?.paymentMethod === PaymentMethod.AuthNetAim;
    }

    openAuthorizeNetTransactionDetails(): void {
        if (!this.isAuthorizeNetPayment()) {
            return;
        }

        this.validateOrderAndPaymentIds(this.orderId, this.paymentId);

        this.dialog.open(PaymentTransactionDetailsDialogComponent, {
            width: '500px',
            disableClose: true,
            data: {
                orderId: this.orderId,
                paymentId: this.paymentId,
            },
        });
    }

    /**
     * Validates that both the order ID and the payment ID are present before attempting to view Authorize.NET transaction details.
     * If either value is missing, an error is thrown to prevent API calls with invalid parameters.
     */
    private validateOrderAndPaymentIds(
        orderId: string | null,
        paymentId: string | null | undefined,
    ): void {
        if (!orderId || !paymentId) {
            throw new Error(
                'Order ID and Payment ID are required to see Authorize.NET transaction details.',
            );
        }
    }
}
